'use client';

import H3 from '@/components/typography/h3';
import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import Link from 'next/link';
import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type FoodQuestionItem = {
    title: string;
    description: string;
    cases: { label: string; href: string }[];
};

interface FoodQuestionCardMobiProps {
    item: FoodQuestionItem;
    index: number;
    activeIndex: number;
    setActiveIndex: (index: number) => void;
}

export default function FoodQuestionCardMobi({
    item,
    index,
    activeIndex,
    setActiveIndex,
}: FoodQuestionCardMobiProps) {
    const { t } = useTranslation();
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!cardRef.current) return;

            const rect = cardRef.current.getBoundingClientRect();
            const cardMid = rect.top + rect.height / 2;
            const viewportMid = window.innerHeight / 2;

            const isCentered =
                Math.abs(cardMid - viewportMid) < rect.height / 2;

            if (isCentered) {
                setActiveIndex(index);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [index, setActiveIndex]);

    const isActive = activeIndex === index;

    return (
        <div
            ref={cardRef}
            className={`flex flex-col gap-[1.87rem] rounded-lg px-[1.4rem] pt-[1.25rem] pb-[2rem] transition-colors duration-300 ${
                isActive
                    ? 'border border-transparent bg-[oklch(0.8153_0.0919_267.98)]'
                    : 'border-primary border'
            }`}
        >
            <H3>{item.title}</H3>
            <P>{item.description}</P>
            <p className="leading-tight break-words whitespace-normal">
                {item.cases.length > 0 && (
                    <Label variant="button" className="mr-1">
                        {t('foodQuestions.cases')}:
                    </Label>
                )}
                {item.cases.map((caseItem, i) => (
                    <Fragment key={i}>
                        <Link href={caseItem.href} className="underline">
                            <Label variant="button" className="inline">
                                {caseItem.label}
                            </Label>
                        </Link>
                        {i < item.cases.length - 1 && ' / '}
                    </Fragment>
                ))}
            </p>
        </div>
    );
}
